import React, { useRef } from 'react';
import type ReactSelect from 'react-select';
import styled from 'styled-components';

import { useFormContext } from 'common/components/form/FormContext';
import FormSection from 'components/lib/form/FormSection';
import FormSubmitButton from 'components/lib/form/FormSubmitButton';
import SelectField from 'components/lib/form/SelectField';
import TextField from 'components/lib/form/TextField';
import FlexContainer from 'components/lib/ui/FlexContainer';

import { CANADA_PROVINCE_OPTIONS } from 'common/constants/canadaProvinceOptions';
import { Country, COUNTRY_OPTIONS } from 'common/constants/onboarding/user';
import STATE_OPTIONS from 'common/constants/stateOptions';

import type { Common_UpdateMyHouseholdMutationVariables } from 'common/generated/graphql';

const SmallField = styled(TextField)`
  flex: 2;
`;

const GrowSelectField = styled(SelectField)`
  flex: 3;
`;

export type HouseholdSettingsInnerFormValues = Common_UpdateMyHouseholdMutationVariables['input'];

export const HouseholdSettingsInnerForm = () => {
  const { values, initialValues, resetForm } = useFormContext<HouseholdSettingsInnerFormValues>();
  const previousCountryValue = useRef<typeof values.country>(initialValues.country);
  const stateSelectRef = useRef<ReactSelect>(null);

  const handleCountryChange = () => {
    if (previousCountryValue.current !== values.country) {
      const newValues: HouseholdSettingsInnerFormValues = {
        ...values,
        address: '',
        city: '',
        state: '',
        zipCode: '',
      };
      stateSelectRef.current?.select.clearValue();

      previousCountryValue.current = values.country;

      resetForm({ values: newValues });
    }
  };

  return (
    <>
      <FormSection>
        <TextField name="name" label="Household Name" required />
      </FormSection>
      <FormSection>
        <SelectField name="country" options={COUNTRY_OPTIONS} onChange={handleCountryChange} />
        <TextField name="address" />
        <TextField name="city" />
        <FlexContainer inline full gap="xxlarge">
          <GrowSelectField
            innerRef={stateSelectRef}
            name="state"
            label={values.country === Country.CANADA ? 'Province' : 'State'}
            options={values.country === Country.CANADA ? CANADA_PROVINCE_OPTIONS : STATE_OPTIONS}
          />
          <SmallField
            name="zipCode"
            label={values.country === Country.CANADA ? 'Postal Code' : 'Zip Code'}
            maxLength={values.country === Country.CANADA ? 7 : 5}
          />
        </FlexContainer>
        <FormSubmitButton>Update Household</FormSubmitButton>
      </FormSection>
    </>
  );
};
